import { FC, useMemo } from "react";
import StarRate from "../../../components/StarRate";
import { IWidgetTemplate } from "../../../interfaces/widget-template";
import { ReachClick } from "../../../utils/core";
import { resolveWidgetV2Style } from "./widgetV2Styles";

/** Card matching the design system's MiniCarousel: quote, then author · stars below. */
const MiniCard: FC<{ title?: string; author?: string; score?: number }> = ({ title, author, score }) => (
  <div style={{ background: "#f7f8fa", borderRadius: 8, padding: "8px 12px" }}>
    <div style={{ fontSize: 11.5 }}>&#8220;{title}&#8221;</div>
    <div className="mt-1 d-flex align-items-center gap-1" style={{ fontSize: 10, color: "#9aa0b4" }}>
      {author ?? "Verified user"} · <StarRate value={score} size={9} />
    </div>
  </div>
);

/** Card matching the design system's "Two-column" cell: stars on top, quote, author below. */
const ColumnCard: FC<{ title?: string; author?: string; score?: number }> = ({ title, author, score }) => (
  <div style={{ background: "#f7f8fa", borderRadius: 8, padding: "8px 10px" }}>
    <StarRate value={score} size={8} />
    <div className="mt-1" style={{ fontSize: 10.5 }}>{title}</div>
    <div className="mt-1" style={{ fontSize: 9.5, color: "#9aa0b4" }}>{author ?? "Verified user"}</div>
  </div>
);

/** Review Carousel (v2) — admin-preview-only (non-sliding here, real embed picks the newest 2). Real embed markup lives in widget-v2.js (renderCarousel). */
const WidgetTemplate19: FC<IWidgetTemplate> = ({ company, review, style }) => {
  const companyId = company?.companyId;
  const resolvedStyle = resolveWidgetV2Style("carousel", style);
  const payload = useMemo(() => ({ companyId, widgetId: 19 as const }), [companyId]);
  const profileUrl = useMemo(
    () => `https://www.trustfinance.com/companies/${companyId}?utm_source=widget&utm_medium=dom&utm_campaign=widget-v2-carousel`,
    [companyId],
  );
  const items = review?.data ?? [];
  const first = items[0];

  if (resolvedStyle === "spotlight") {
    return (
      <a
        href={profileUrl}
        target="_blank"
        rel="noopener"
        onClick={() => ReachClick(payload)}
        className="d-block text-center"
        style={{ textDecoration: "none", color: "#1c1c2e", background: "#f7f8fa", borderRadius: 8, padding: "12px 16px", maxWidth: 280, margin: "0 auto" }}
      >
        <div className="d-flex justify-content-center mb-1"><StarRate value={first?.score} size={11} /></div>
        <div style={{ fontSize: 12, fontStyle: "italic" }}>&#8220;{first?.title}&#8221;</div>
        <div className="mt-1" style={{ fontSize: 10, color: "#9aa0b4" }}>{first?.user?.displayName ?? "Verified user"} · verified</div>
      </a>
    );
  }

  return (
    <a
      href={profileUrl}
      target="_blank"
      rel="noopener"
      onClick={() => ReachClick(payload)}
      className="d-block"
      style={{ textDecoration: "none", color: "#1c1c2e", maxWidth: resolvedStyle === "two-column" ? 320 : 280, margin: "0 auto" }}
    >
      <div style={resolvedStyle === "two-column" ? { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 } : { display: "grid", gap: 8 }}>
        {items.slice(0, 2).map((item) =>
          resolvedStyle === "two-column" ? (
            <ColumnCard key={item.id} title={item.title} author={item.user?.displayName} score={item.score} />
          ) : (
            <MiniCard key={item.id} title={item.title} author={item.user?.displayName} score={item.score} />
          ),
        )}
      </div>
    </a>
  );
};

export default WidgetTemplate19;
